import type { FormInstance } from 'antd';
import { message } from 'antd';
import { Moment } from 'moment';
import { create } from 'zustand';

import produce from 'immer';
import GetProductQualities from '~/@core/application/useCases/inputs/product/getProductQualities';
import GetProductsPreferenceColor from '~/@core/application/useCases/inputs/product/getProductsPreferenceColor';
import Customers from '~/@core/domain/model/customer/Customers';
import type IProductCategory from '~/@core/domain/model/product/ProductCategory';
import ProductPreferenceColor from '~/@core/domain/model/product/ProductPreferenceColor';
import ProductQuality from '~/@core/domain/model/product/ProductQuality';
import type Site from '~/@core/domain/model/site/Site';
import { fetchSites as fetchSitesService } from '~/@core/infrastructure/api/services/siteService';
import { PermissionNames } from '~/constants/permissionNames';
import { createInitialTableState, INITIAL_DATA_STATE } from '~/constants/table';
import { SaleStatement } from '~/domain/api';
import { SaleStatementDateType } from '~/domain/enum/salesStatement/saleStatementDateType.enum';
import { RecipientDetailed } from '~/domain/model';
import { ISiteIdParams } from '~/domain/model/PriceReport/PriceReportService';
import type IProductGroupWithCategories from '~/domain/model/PriceReport/ProductGroupWithCategories';
import type { IProductSimple } from '~/domain/model/PriceReport/ProductSimple';
import { ISalesChannel } from '~/domain/model/PriceReport/SaleChannel';
import {
  IPagedSalesStatementReportParams,
  ISalesStatementReportParams,
} from '~/domain/model/SalesStatement/SalesStatement';
import { getAllCustomers } from '~/services/producer/customer/CustomerService';
import {
  getProductCategories,
  getProductGroups,
  getProductRecipients,
  getProductsSimple,
  getSaleChannels as getSaleChannelsService,
} from '~/services/producer/PriceReport/PriceReportService';
import { getTable } from '~/services/producer/SalesStatement/SalesStatementService';
import { ITableDataState, ITableState } from '~/types/table';

export type IFormFilter = {
  saleChannels: string[];
  siteIds: number[];
  dateType: SaleStatementDateType | undefined;
  filteredDate: Moment | null;
  productIds: number[];
  categoryIds: number[];
  groupIds: number[];
  sizes: string[];
  colorIds: number[];
  quality: string | null;
  customerIds: number[];
  origin: string | null;
};

export type ITable = ITableState<SaleStatement>;

export type IActions = {
  setData: (fn: (state: ISalesStatement) => void) => void;
  setFormInstance: (form: Partial<FormInstance<IFormFilter>>) => void;
  fetchSites: () => Promise<Site[]>;
  fetchSaleChannels: () => Promise<ISalesChannel[]>;
  getCustomers: () => Promise<Customers[]>;
  getCategories: () => Promise<IProductCategory[]>;
  getProducts: () => Promise<IProductSimple[]>;
  getSizes: () => Promise<RecipientDetailed[]>;
  getGroups: () => Promise<IProductGroupWithCategories[]>;
  getColors: () => Promise<ProductPreferenceColor[]>;
  getQualities: () => Promise<ProductQuality[]>;
  mappedSiteIds: () => number[];
  resetStatesToDefault: () => void;
  onSelectOrClearSite: () => void;
  toggleFilterVisibility: () => void;
  loadMainFilters: () => void;
  loadHiddenFilters: () => void;
  handleSearch: () => void;
  handleOrder: () => void;
};

export type IGetters = {
  getParams(): ISalesStatementReportParams;
  getPagedParams(): IPagedSalesStatementReportParams;
};

export type IFilters = {
  saleChannels: ITableDataState<ISalesChannel>;
  sites: ITableDataState<Site>;
  products: ITableDataState<IProductSimple>;
  categories: ITableDataState<IProductCategory>;
  groups: ITableDataState<IProductGroupWithCategories>;
  sizes: ITableDataState<RecipientDetailed>;
  colors: ITableDataState<ProductPreferenceColor>;
  qualities: ITableDataState<ProductQuality>;
  customers: ITableDataState<Customers>;
  formFilter: IFormFilter;
};

export type ISalesStatement = {
  table: ITable;
  filters: IFilters;
  formFilterInstance: Partial<FormInstance<IFormFilter>> | null;
  isMainFiltersSuccessful: boolean;
  isHiddenFiltersSucessful: boolean;
  showHiddenFilters: boolean;
} & IActions &
  IGetters;

export const defaultColumnOrderBy = 'commercialDate';

export const initialStateTable: ITable = createInitialTableState<SaleStatement>({
  orderBy: defaultColumnOrderBy,
  pageSize: 100,
});

export const initialStateFilters: IFilters = {
  formFilter: {
    saleChannels: [],
    siteIds: [],
    productIds: [],
    categoryIds: [],
    groupIds: [],
    sizes: [],
    colorIds: [],
    quality: null,
    filteredDate: null,
    dateType: undefined,
    customerIds: [],
    origin: null,
  },
  saleChannels: INITIAL_DATA_STATE,
  sites: INITIAL_DATA_STATE,
  products: INITIAL_DATA_STATE,
  categories: INITIAL_DATA_STATE,
  groups: INITIAL_DATA_STATE,
  sizes: INITIAL_DATA_STATE,
  colors: INITIAL_DATA_STATE,
  qualities: INITIAL_DATA_STATE,
  customers: INITIAL_DATA_STATE,
};

export const initialState = {
  formFilterInstance: null,
};

const PermissionName = PermissionNames.Reports.MenuSalesStatement;

const hasElements = <T>(_array: T[]): boolean => Array.isArray(_array) && _array.length > 0;

const useSaleStatementStore = create<ISalesStatement>()((set, get) => ({
  // States
  table: initialStateTable,
  filters: initialStateFilters,
  formFilterInstance: initialState.formFilterInstance,
  isMainFiltersSuccessful: false,
  isHiddenFiltersSucessful: false,
  showHiddenFilters: false,

  // Setters
  setData: fn => set(produce(fn)),
  setFormInstance: form => {
    set({ formFilterInstance: form });
  },

  // Getters
  getParams: () => {
    const formValues = get().formFilterInstance?.getFieldsValue?.();

    const chosenDate = formValues?.filteredDate?.format('yyyy-MM-DD') ?? undefined;

    const commercialDate =
      formValues?.dateType === SaleStatementDateType.Commercial ? chosenDate : undefined;

    const transactionDate =
      formValues?.dateType === SaleStatementDateType.Transaction ? chosenDate : undefined;

    return {
      CommercialDate: commercialDate,
      TransactionDate: transactionDate,
      SaleChannels: formValues?.saleChannels ?? [],
      SiteIds: formValues?.siteIds ?? [],
      ProductIds: formValues?.productIds ?? [],
      CategoryIds: formValues?.categoryIds ?? [],
      GroupIds: formValues?.groupIds ?? [],
      Sizes: formValues?.sizes ?? [],
      ColorIds: formValues?.colorIds ?? [],
      Quality: formValues?.quality ?? undefined,
      PermissionName,
    } as ISalesStatementReportParams;
  },
  getPagedParams: () => {
    const tableParams = get().table.tableParams;

    return {
      Data: get().getParams(),
      PageSize: tableParams.pagination.pageSize ?? 100,
      PageNumber: tableParams.pagination.current ?? 1,
      OrderBy: tableParams.orderBy ?? defaultColumnOrderBy,
      Sort: tableParams.sort ?? 0,
    } as IPagedSalesStatementReportParams;
  },

  // Actions
  fetchSites: async () => {
    try {
      get().setData(state => {
        state.filters.sites.isLoading = true;
        state.filters.sites.isSuccessful = false;
        state.filters.sites.isError = false;
      });

      const data = await fetchSitesService(PermissionName);

      get().setData(state => {
        state.filters.sites.data = data || [];
        state.filters.sites.isSuccessful = true;
        state.filters.sites.isError = false;
        state.filters.sites.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      get().setData(state => {
        state.filters.sites.data = [];
        state.filters.sites.isSuccessful = false;
        state.filters.sites.isError = true;
        state.filters.sites.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  fetchSaleChannels: async () => {
    const { setData } = get();

    try {
      setData(state => {
        state.filters.saleChannels.isLoading = true;
        state.filters.saleChannels.isSuccessful = false;
        state.filters.saleChannels.isError = false;
      });

      const response = await getSaleChannelsService();
      const data = response?.data ?? [];

      setData(state => {
        state.filters.saleChannels.data = data;
        state.filters.saleChannels.isSuccessful = true;
        state.filters.saleChannels.isError = false;
        state.filters.saleChannels.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.saleChannels.data = [];
        state.filters.saleChannels.isSuccessful = false;
        state.filters.saleChannels.isError = true;
        state.filters.saleChannels.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getCustomers: async () => {
    try {
      get().setData(state => {
        state.filters.customers.isLoading = true;
        state.filters.customers.isSuccessful = false;
        state.filters.customers.isError = false;
      });

      const { data } = await getAllCustomers();

      get().setData(state => {
        state.filters.customers.data = data ?? [];
        state.filters.customers.isSuccessful = true;
        state.filters.customers.isError = false;
        state.filters.customers.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      get().setData(state => {
        state.filters.customers.data = [];
        state.filters.customers.isSuccessful = false;
        state.filters.customers.isError = true;
        state.filters.customers.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getCategories: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.categories.isLoading = true;
      state.filters.categories.isSuccessful = false;
      state.filters.categories.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(),
      } as ISiteIdParams;

      const { data } = await getProductCategories(params);

      setData(state => {
        state.filters.categories.data = data;
        state.filters.categories.isSuccessful = true;
        state.filters.categories.isError = false;
        state.filters.categories.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.categories.data = [];
        state.filters.categories.isSuccessful = false;
        state.filters.categories.isError = true;
        state.filters.categories.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getGroups: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.groups.isLoading = true;
      state.filters.groups.isSuccessful = false;
      state.filters.groups.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(),
      } as ISiteIdParams;

      const { data } = await getProductGroups(params);

      setData(state => {
        state.filters.groups.data = data;
        state.filters.groups.isSuccessful = true;
        state.filters.groups.isError = false;
        state.filters.groups.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.groups.data = [];
        state.filters.groups.isSuccessful = false;
        state.filters.groups.isError = true;
        state.filters.groups.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getProducts: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.products.isLoading = true;
      state.filters.products.isSuccessful = false;
      state.filters.products.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(),
      } as ISiteIdParams;

      const { data } = await getProductsSimple(params);

      setData(state => {
        state.filters.products.data = data;
        state.filters.products.isSuccessful = true;
        state.filters.products.isError = false;
        state.filters.products.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.products.data = [];
        state.filters.products.isSuccessful = false;
        state.filters.products.isError = true;
        state.filters.products.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getSizes: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.sizes.isLoading = true;
      state.filters.sizes.isSuccessful = false;
      state.filters.sizes.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(),
      } as ISiteIdParams;

      const { data } = await getProductRecipients(params);

      setData(state => {
        state.filters.sizes.data = data;
        state.filters.sizes.isSuccessful = true;
        state.filters.sizes.isError = false;
        state.filters.sizes.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.sizes.data = [];
        state.filters.sizes.isSuccessful = false;
        state.filters.sizes.isError = true;
        state.filters.sizes.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getColors: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.colors.isLoading = true;
      state.filters.colors.isSuccessful = false;
      state.filters.colors.isError = false;
    });
    try {
      const data = await new GetProductsPreferenceColor().execute();

      setData(state => {
        state.filters.colors.data = data;
        state.filters.colors.isSuccessful = true;
        state.filters.colors.isError = false;
        state.filters.colors.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.colors.data = [];
        state.filters.colors.isSuccessful = false;
        state.filters.colors.isError = true;
        state.filters.colors.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getQualities: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.qualities.isLoading = true;
      state.filters.qualities.isSuccessful = false;
      state.filters.qualities.isError = false;
    });
    try {
      const data = await new GetProductQualities().execute();

      setData(state => {
        state.filters.qualities.data = data;
        state.filters.qualities.isSuccessful = true;
        state.filters.qualities.isError = false;
        state.filters.qualities.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.qualities.data = [];
        state.filters.qualities.isSuccessful = false;
        state.filters.qualities.isError = true;
        state.filters.qualities.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  mappedSiteIds: () => {
    const sites = get().filters?.sites?.data ?? [];
    const siteIdsFormSelected = get().filters?.formFilter?.siteIds ?? [];

    if (!hasElements(siteIdsFormSelected)) {
      return sites.map(site => site.id);
    }

    return siteIdsFormSelected;
  },
  handleSearch: () => {
    const { setData } = get();
    setData(state => {
      state.table.tableParams.pagination.current = 1;
    });

    get().handleOrder();
  },
  handleOrder: async () => {
    const { setData, getPagedParams } = get();

    try {
      setData(state => {
        state.table.isLoading = true;
        state.table.isSuccessful = false;
        state.table.isError = false;
      });

      const params = getPagedParams();

      const response = await getTable(params);

      const data = response?.data || { results: [], totalCount: 0 };

      setData(state => {
        state.table.data = data.results || [];
        state.table.tableParams.pagination.total = data.totalCount || 0;
        state.table.isSuccessful = true;
        state.table.isError = false;
        state.table.isLoading = false;
      });

      return Promise.resolve(data.results);
    } catch (error) {
      setData(state => {
        state.table.data = [];
        state.table.tableParams.pagination.total = 0;
        state.table.isSuccessful = false;
        state.table.isError = true;
        state.table.isLoading = false;
      });

      message.error('Não foi possível carregar a tabela.');

      return Promise.reject(error);
    }
  },
  onSelectOrClearSite: () => {
    const {
      showHiddenFilters,
      getCategories,
      getGroups,
      getProducts,
      getSizes,
      getColors,
      getQualities,
    } = get();

    if (showHiddenFilters) {
      getCategories();
      getGroups();
      getProducts();
      getSizes();
      getColors();
      getQualities();
    }
  },
  toggleFilterVisibility: () => {
    const { isMainFiltersSuccessful, loadHiddenFilters, showHiddenFilters, setData } = get();

    if (showHiddenFilters) {
      setData(state => {
        state.showHiddenFilters = false;
      });

      return;
    }

    if (isMainFiltersSuccessful) {
      const wasFilterHidden = !showHiddenFilters;

      setData(state => {
        state.showHiddenFilters = !showHiddenFilters;
      });

      if (wasFilterHidden) {
        loadHiddenFilters();
      }
    }
  },
  loadMainFilters: async () => {
    try {
      const [sitesResult, saleChannelsResult] = await Promise.allSettled([
        get().fetchSites(),
        get().fetchSaleChannels(),
      ]);

      const isSuccessful = (
        result:
          | PromiseRejectedResult
          | PromiseFulfilledResult<ISalesChannel[]>
          | PromiseFulfilledResult<Site[]>,
      ) => result.status === 'fulfilled';

      const isMainFiltersSuccessful = isSuccessful(sitesResult) && isSuccessful(saleChannelsResult);

      get().setData(state => {
        state.isMainFiltersSuccessful = isMainFiltersSuccessful;
      });

      if (!isMainFiltersSuccessful) {
        message.warn('Erro ao carregar alguns filtros principais.');
      }
    } catch (error) {
      console.error('Erro ao carregar filtros principais.', error);
    }
  },
  loadHiddenFilters: async () => {
    const {
      getCategories,
      getGroups,
      getProducts,
      getSizes,
      getColors,
      getQualities,
      getCustomers,
    } = get();

    try {
      const [
        categoriesResult,
        groupsResult,
        productsResult,
        sizesResult,
        colorsResult,
        qualitiesResult,
        customersResult,
      ] = await Promise.allSettled([
        getCategories(),
        getGroups(),
        getProducts(),
        getSizes(),
        getColors(),
        getQualities(),
        getCustomers(),
      ]);

      const isSuccessful = (
        result:
          | PromiseRejectedResult
          | PromiseFulfilledResult<IProductCategory[]>
          | PromiseFulfilledResult<IProductSimple[]>
          | PromiseFulfilledResult<RecipientDetailed[]>
          | PromiseFulfilledResult<IProductGroupWithCategories[]>
          | PromiseFulfilledResult<ProductPreferenceColor[]>
          | PromiseFulfilledResult<ProductQuality[]>,
      ) => result.status === 'fulfilled';

      const allSuccessful =
        isSuccessful(categoriesResult) &&
        isSuccessful(groupsResult) &&
        isSuccessful(productsResult) &&
        isSuccessful(sizesResult) &&
        isSuccessful(colorsResult) &&
        isSuccessful(qualitiesResult) &&
        isSuccessful(customersResult);

      get().setData(state => {
        state.isHiddenFiltersSucessful = allSuccessful;
      });

      if (!allSuccessful) {
        console.warn('Erro ao carregar alguns filtros avançados.');
      }
    } catch (error) {
      console.error('Erro ao carregar filtros avançados.', error);
    }
  },
  resetStatesToDefault: () => {
    const { setData } = get();
    const { formFilterInstance } = get();

    setData(state => {
      state.table = initialStateTable;
      state.filters = initialStateFilters;
      state.formFilterInstance = formFilterInstance;
      state.isMainFiltersSuccessful = false;
      state.isHiddenFiltersSucessful = false;
      state.showHiddenFilters = false;
    });
  },
}));

export default useSaleStatementStore;
